'use client';

import { EntityRatingCommentCount, Professional, ProfessionalSchedule, ProfessionalScheduleProcedures } from '@medprev-web-app/api-graphql-client';

import { ProfessionalAsideCard } from './professional-aside-card.component';
import { ProfessionalHeader } from './professional-header.component';
import { ProfessionalSchedulesSection } from './professional-schedules-section.component';
import { TabProfessional } from './tab-professional.component';

import { Breadcrumb } from '@/components/ui/breadcrumb.component';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { ContentContainer } from '@/components/ui/content-container.component';
import { RatingComment } from '@/types';
import { Card } from '@/components/ui/card.component';
import { ErrorBoundary } from '@/app/_components/error-boundary.component';
import { useNewSchedule } from '@/feature-flags/hooks/use-new-schedule.hook';
import { useProfessionalProcedures } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/[slug]/_hooks/use-professional-procedures.hook';

interface ProfessionalWrapperProps {
  professional: Professional;
  commentsCount?: EntityRatingCommentCount | null;
  procedures: ProfessionalScheduleProcedures[];
  inPersonData: ProfessionalSchedule[];
  telehealthData: ProfessionalSchedule[];
  initialComments?: RatingComment[];
}

export const resolveProfessionalName = (professional: Professional): string => {
  const { name, honorific } = professional;

  if (!honorific) {
    return name as string;
  }

  return formatToTitleCase(`${honorific} ${name}`);
};

export function ProfessionalWrapper({
  initialComments,
  professional,
  commentsCount,
  inPersonData,
  procedures,
  telehealthData,
}: ProfessionalWrapperProps) {
  const { enabled: newScheduleEnabled, loading: isFlagLoading } = useNewSchedule();
  const usesLegacyAside = isFlagLoading || !newScheduleEnabled;
  const professionalProcedures = useProfessionalProcedures(professional.id as string, { enabled: !usesLegacyAside });
  const hasSchedulesSection = professionalProcedures.isLoading || professionalProcedures.availableChannels.length > 0;

  const breadcrumbsItems = [
    {
      label: 'Profissionais',
      href: '/profissionais/',
    },
    {
      label: resolveProfessionalName(professional as Professional),
    },
  ];

  return (
    <>
      <Breadcrumb list={breadcrumbsItems} abbreviated />

      <ErrorBoundary errorMessage="Professional Wrapper">
        <ContentContainer className="md:py-6 bg-white md:bg-transparent">
          <div className="block mx-auto sm:max-w-lg md:max-w-full md:w-auto xl:flex lg:flex-row lg:items-start gap-6">
            <section className="flex-1">
              <Card className="px-0 border-0 md:border md:px-10 pt-6 pb-0 md:pt-8 3xl:pt-10  w-full lg:min-w-[37.5rem] 3xl:min-w-[46.5rem]">
                <ProfessionalHeader
                  professional={professional}
                  commentsCount={commentsCount}
                  shouldAddLightbox
                  hasSchedulesSection={hasSchedulesSection}
                />
                <TabProfessional
                  professional={professional}
                  countComments={commentsCount}
                  inPersonData={inPersonData}
                  telehealthData={telehealthData}
                  initialComments={initialComments!}
                />
              </Card>
            </section>

            {usesLegacyAside
              ? (
                  <ProfessionalAsideCard professional={professional} procedure={procedures} className="hidden xl:block" />
                )
              : (
                  <ProfessionalSchedulesSection
                    procedures={professionalProcedures}
                    className="mt-4 w-full flex-1 xl:sticky xl:top-6 xl:mt-0 xl:max-w-[32rem]"
                  />
                )}
          </div>
        </ContentContainer>
      </ErrorBoundary>
    </>
  );
}
